
import React, { useState, useEffect, useMemo } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { supabase } from '../services/supabase';
import { THAI_LABELS } from '../constants';
import { HealthLog } from '../types';

interface DashboardProps {
  isDarkMode?: boolean;
}

const Dashboard: React.FC<DashboardProps> = ({ isDarkMode }) => {
  const [logs, setLogs] = useState<HealthLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    fetchLogs();

    // 1. Setup Local Refresh Listener (from same browser tab)
    const handleLocalRefresh = () => fetchLogs();
    window.addEventListener('refreshData', handleLocalRefresh);

    // 2. Setup Supabase Realtime Subscription (from other clients/devices)
    let channel: any;
    const setupRealtime = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const topic = `user:${user.id}:health_logs`;
      channel = supabase.channel(topic, {
        config: {
          broadcast: { self: true, ack: true },
          presence: { key: user.id },
          private: true
        }
      });

      // Listen for all broadcast events on this topic
      channel.on('broadcast', { event: '*' }, (payload: any) => {
        console.log('Realtime broadcast received:', payload);
        fetchLogs(); // Trigger refresh when broadcast received
      });

      // Also listen for database changes directly if broadcast is not sent
      // This ensures double reliability
      channel.on(
        'postgres_changes', 
        { event: '*', schema: 'public', table: 'health_logs', filter: `user_id=eq.${user.id}` },
        () => fetchLogs()
      );

      const { error } = await channel.subscribe();
      if (error) {
        console.error('Subscription error:', error);
      } else {
        console.log(`Subscribed to realtime topic: ${topic}`);
      }
    };

    setupRealtime();

    // Cleanup on unmount
    return () => {
      window.removeEventListener('refreshData', handleLocalRefresh);
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, []);

  const fetchLogs = async () => {
    try {
      // Keep silent refresh if logs already exist to improve UX
      if (logs.length === 0) setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data, error } = await supabase
          .from('health_logs')
          .select('*')
          .order('date', { ascending: true });
        
        if (error) throw error;
        if (data) setLogs(data);
      }
    } catch (err) {
      console.error("Error fetching logs for dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const logTime = new Date(log.date).getTime();
      const start = startDate ? new Date(startDate).getTime() : -Infinity;
      const end = endDate ? new Date(endDate).getTime() : Infinity;
      return logTime >= start && logTime <= end;
    });
  }, [logs, startDate, endDate]);

  const latest = logs.length > 0 ? logs[logs.length - 1] : null;
  const previous = logs.length > 1 ? logs[logs.length - 2] : null;

  const weightDiff = latest && previous ? (latest.weight - previous.weight).toFixed(1) : '0';
  const isWeightDown = parseFloat(weightDiff) < 0;

  const formatThaiDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('th-TH', { month: 'short', year: '2-digit' });
  };

  const anomalies = useMemo(() => {
    if (!latest) return [];
    const alerts = [];
    if (latest.systolic >= 140 || latest.diastolic >= 90) alerts.push({ type: 'BP', label: 'ความดันโลหิตสูงเกินเกณฑ์', severity: 'high' });
    if (latest.ldl >= 160) alerts.push({ type: 'LDL', label: 'ไขมันเลว (LDL) สูงผิดปกติ', severity: 'high' });
    if (latest.fbs && latest.fbs >= 126) alerts.push({ type: 'FBS', label: 'ระดับน้ำตาลในเลือดสูง (เสี่ยงเบาหวาน)', severity: 'high' });
    if (latest.bmi >= 30) alerts.push({ type: 'BMI', label: 'ดัชนีมวลกายอยู่ในเกณฑ์อ้วน', severity: 'medium' });
    return alerts;
  }, [latest]);

  const getStatusColor = (sys: number) => {
    if (sys >= 140) return 'text-red-500';
    if (sys >= 130) return 'text-amber-500';
    return isDarkMode ? 'text-green-400' : 'text-green-500';
  };

  const AlertBadge = ({ severity }: { severity: string }) => (
    <div className={`flex items-center justify-center w-5 h-5 rounded-full ${severity === 'high' ? 'bg-red-500 animate-pulse' : 'bg-amber-500'} text-white`}>
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    </div>
  );

  if (loading && logs.length === 0) return (
    <div className="flex flex-col items-center justify-center p-20 space-y-4">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">กำลังดึงข้อมูลสุขภาพ...</p>
    </div>
  );

  if (logs.length === 0) return (
    <div className="p-10 text-center space-y-4">
      <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto text-slate-300 dark:text-slate-600">
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
      </div>
      <h3 className="text-lg font-bold text-slate-700 dark:text-slate-200">ยังไม่มีข้อมูลบันทึกในระบบ</h3>
      <p className="text-sm text-slate-400 dark:text-slate-500">เริ่มบันทึกข้อมูลสุขภาพของคุณที่แท็บ "สมุดบันทึก" เพื่อดูผลวิเคราะห์</p>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Critical Alerts Section */}
      {anomalies.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-100 dark:border-red-900/30 p-4 rounded-2xl space-y-2 animate-in slide-in-from-top-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="p-1.5 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded-lg">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </span>
            <h4 className="text-sm font-black text-red-700 dark:text-red-400 uppercase tracking-wider">พบข้อบ่งชี้ทางสุขภาพที่ควรเฝ้าระวัง</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {anomalies.map((alert, idx) => (
              <div key={idx} className="bg-white dark:bg-slate-800 px-3 py-1.5 rounded-xl border border-red-100 dark:border-red-900/30 flex items-center gap-2 shadow-sm">
                <div className={`w-2 h-2 rounded-full ${alert.severity === 'high' ? 'bg-red-500' : 'bg-amber-500'}`}></div>
                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">{alert.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800 relative overflow-hidden transition-colors">
          <div className="flex justify-between items-start">
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tight">{THAI_LABELS.WEIGHT}</p>
            {latest && latest.bmi >= 30 && <AlertBadge severity="medium" />}
          </div>
          <p className="text-2xl font-black text-blue-600 dark:text-blue-400">{latest?.weight.toFixed(1) || '--'}</p>
          <p className={`text-[10px] font-bold ${isWeightDown ? 'text-green-500 dark:text-green-400' : 'text-red-400'}`}>
            {isWeightDown ? '↓' : '↑'} {Math.abs(parseFloat(weightDiff))}kg จากครั้งก่อน
          </p>
        </div>
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800 transition-colors">
          <div className="flex justify-between items-start">
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tight">{THAI_LABELS.BP}</p>
            {latest && (latest.systolic >= 140 || latest.diastolic >= 90) && <AlertBadge severity="high" />}
          </div>
          <p className={`text-2xl font-black ${latest ? getStatusColor(latest.systolic) : 'text-slate-400 dark:text-slate-600'}`}>
            {latest ? `${latest.systolic}/${latest.diastolic}` : '--/--'}
          </p>
          <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500">
            {latest && latest.systolic >= 140 ? 'ความดันสูง' : latest && latest.systolic >= 130 ? 'ค่อนข้างสูง' : 'ปกติ'}
          </p>
        </div>
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800 transition-colors">
          <div className="flex justify-between items-start">
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tight">{THAI_LABELS.LDL}</p>
            {latest && latest.ldl >= 160 && <AlertBadge severity="high" />}
          </div>
          <p className="text-2xl font-black text-amber-500">{latest?.ldl || '--'}</p>
          <p className="text-[10px] font-bold text-amber-500">
            {latest && latest.ldl > 130 ? 'เริ่มสูง' : 'ปกติ'}
          </p>
        </div>
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800 transition-colors">
          <div className="flex justify-between items-start">
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tight">{THAI_LABELS.BMI}</p>
            {latest && latest.bmi >= 30 && <AlertBadge severity="medium" />}
          </div>
          <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400">{latest?.bmi.toFixed(1) || '--'}</p>
          <p className="text-[10px] font-bold text-indigo-500 dark:text-indigo-400">
            {latest && latest.bmi >= 25 ? 'ท้วม/อ้วน' : 'ปกติ'}
          </p>
        </div>
      </div>

      {/* Main History Chart */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 transition-colors">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{THAI_LABELS.HISTORY}</h3>
          
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 transition-colors">
              <input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-transparent text-[10px] font-bold text-slate-600 dark:text-slate-300 outline-none"
              />
              <span className="mx-1 text-slate-300 dark:text-slate-600 text-[10px]">-</span>
              <input 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-transparent text-[10px] font-bold text-slate-600 dark:text-slate-300 outline-none"
              />
            </div>
            {(startDate || endDate) && (
              <button 
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="text-[10px] font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 underline"
              >
                ล้างตัวกรอง
              </button>
            )}
          </div>
        </div>
        
        <div className="h-[320px] w-full">
          {filteredLogs.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={filteredLogs} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? "#334155" : "#f1f5f9"} />
                <XAxis 
                  dataKey="date" 
                  stroke={isDarkMode ? "#64748b" : "#94a3b8"} 
                  fontSize={10} 
                  fontWeight="bold"
                  tickLine={false} 
                  axisLine={false} 
                  tickFormatter={formatThaiDate}
                  dy={10}
                />
                <YAxis stroke={isDarkMode ? "#64748b" : "#94a3b8"} fontSize={10} fontWeight="bold" tickLine={false} axisLine={false} />
                <Tooltip 
                  labelFormatter={(label) => new Date(label).toLocaleDateString('th-TH', { dateStyle: 'long' })}
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: 'none', 
                    backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', 
                    fontSize: '12px' 
                  }}
                  itemStyle={{ color: isDarkMode ? '#cbd5e1' : '#475569' }}
                />
                <Legend 
                  verticalAlign="top" 
                  align="right" 
                  height={36} 
                  iconType="circle"
                  wrapperStyle={{ fontSize: '10px', fontWeight: 'bold', paddingBottom: '20px' }}
                />
                <Line 
                  name="น้ำหนัก (กก.)" 
                  type="monotone" 
                  dataKey="weight" 
                  stroke="#3b82f6" 
                  strokeWidth={3} 
                  dot={{ r: 4, strokeWidth: 2, fill: isDarkMode ? '#1e293b' : '#fff' }} 
                  activeDot={{ r: 6 }} 
                  animationDuration={1000}
                />
                <Line 
                  name="ความดัน SYS" 
                  type="monotone" 
                  dataKey="systolic" 
                  stroke="#ef4444" 
                  strokeWidth={3} 
                  dot={{ r: 4, strokeWidth: 2, fill: isDarkMode ? '#1e293b' : '#fff' }} 
                  activeDot={{ r: 6 }} 
                  animationDuration={1000}
                />
                <Line 
                  name="ความดัน DIA" 
                  type="monotone" 
                  dataKey="diastolic" 
                  stroke="#10b981" 
                  strokeWidth={3} 
                  dot={{ r: 4, strokeWidth: 2, fill: isDarkMode ? '#1e293b' : '#fff' }} 
                  activeDot={{ r: 6 }} 
                  animationDuration={1000}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 dark:text-slate-600 italic">
              <p>ไม่พบข้อมูลในช่วงเวลาที่เลือก</p>
            </div>
          )}
        </div>
      </div>

      {/* Advice Section */}
      <div className="bg-gradient-to-br from-indigo-500 to-blue-600 dark:from-indigo-600 dark:to-blue-700 p-6 rounded-2xl text-white shadow-lg shadow-blue-100 dark:shadow-none transition-all">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-white/20 p-2 rounded-lg">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h4 className="font-bold">เป้าหมายสุขภาพ</h4>
        </div>
        <p className="text-sm opacity-90 leading-relaxed font-medium">
          ระบบจะแสดงเป้าหมายที่นี่เมื่อคุณเริ่มทำนายผลสุขภาพด้วย AI ในแท็บ "ทำนาย AI" หรือได้รับแจ้งข้อมูลเรียลไทม์จากระบบ
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
